import React from 'react';

/**
 * Ícone animado de erro/aviso, irmão do AnimatedCheck: o círculo faz um "pop",
 * o glifo é desenhado e por fim um anel percorre a borda — one-shot ao montar.
 *
 * variant="error"   → X vermelho (falha/link inválido/cancelado).
 * variant="warning" → "!" âmbar (aviso recuperável, ex.: link expirado).
 *
 * Respeita prefers-reduced-motion: sem animação, mostrando direto o estado final.
 * `className` extra vai no wrapper (ex.: espaçamento específico da tela).
 */
export default function AnimatedError({ variant = 'error', className = '' }) {
  const isWarning = variant === 'warning';
  const bg = isWarning ? 'bg-amber-100' : 'bg-red-100';
  const color = isWarning ? 'text-amber-500' : 'text-red-600';
  return (
    <div className={`mx-auto mb-4 ${className}`}>
      <style>{`
        @keyframes wa-err-pop { 0%{transform:scale(0.5);opacity:0;} 60%{transform:scale(1.08);opacity:1;} 100%{transform:scale(1);} }
        @keyframes wa-err-draw { to { stroke-dashoffset: 0; } }
        @keyframes wa-err-ring { to { stroke-dashoffset: 0; } }
        @keyframes wa-err-dot { 0%{transform:scale(0);opacity:0;} 70%{transform:scale(1.2);opacity:1;} 100%{transform:scale(1);opacity:1;} }
        .wa-err-pop { animation: wa-err-pop 0.4s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .wa-err-x1 { stroke-dasharray: 15; stroke-dashoffset: 15; animation: wa-err-draw 0.25s ease-out 0.25s forwards; }
        .wa-err-x2 { stroke-dasharray: 15; stroke-dashoffset: 15; animation: wa-err-draw 0.25s ease-out 0.45s forwards; }
        .wa-err-bar { stroke-dasharray: 9; stroke-dashoffset: 9; animation: wa-err-draw 0.3s ease-out 0.25s forwards; }
        .wa-err-dot { transform-origin: center; transform-box: fill-box; opacity: 0; animation: wa-err-dot 0.25s cubic-bezier(0.22, 1, 0.36, 1) 0.55s forwards; }
        .wa-err-ring { stroke-dasharray: 239; stroke-dashoffset: 239; animation: wa-err-ring 0.5s ease-out 0.65s forwards; }
        @media (prefers-reduced-motion: reduce) {
          .wa-err-pop { animation: none; }
          .wa-err-x1, .wa-err-x2, .wa-err-bar { animation: none; stroke-dashoffset: 0; }
          .wa-err-dot { animation: none; opacity: 1; }
          .wa-err-ring { animation: none; stroke-dashoffset: 0; }
        }
      `}</style>
      <div className={`wa-err-pop relative w-20 h-20 rounded-full ${bg} flex items-center justify-center mx-auto`}>
        <svg className={`absolute inset-0 w-full h-full -rotate-90 ${color}`} viewBox="0 0 80 80" fill="none" aria-hidden="true">
          <circle className="wa-err-ring" cx="40" cy="40" r="38" stroke="currentColor" strokeWidth={3} strokeLinecap="round" />
        </svg>
        <svg className={`w-10 h-10 ${color}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          {isWarning ? (
            <>
              <path className="wa-err-bar" d="M12 5 L12 13" />
              <circle className="wa-err-dot" cx="12" cy="17.5" r="1.3" fill="currentColor" stroke="none" />
            </>
          ) : (
            <>
              <path className="wa-err-x1" d="M7 7 L17 17" />
              <path className="wa-err-x2" d="M17 7 L7 17" />
            </>
          )}
        </svg>
      </div>
    </div>
  );
}
